/**
 * Manos marketing-flag fetch for the web-landing surface (F7 / ADR-059-addendum-003).
 *
 * web-landing has no App Check and App Check is enforced on Firestore (#1189), so it
 * cannot read `feature_flags/manos` directly. Instead it fetches the public read-only
 * `getManosPublicFlags` CF, which reads the canonical doc live (Admin SDK) and returns the
 * resolved public booleans. Single source of truth preserved; the kill switch reaches
 * marketing within the CF's cache TTL. Fail-closed: any error / missing config → all-OFF.
 *
 * URL is derived from the already-present VITE_FIREBASE_PROJECT_ID (no new env, no secret):
 * the proven `https://us-central1-<project>.cloudfunctions.net/<fn>` pattern.
 */
import { captureException } from './sentry';

export interface ManosPublicFlags {
  master: boolean;
  directory: boolean;
  map: boolean;
  central: boolean;
  share: boolean;
  marketing: boolean;
}

export const MANOS_FLAGS_ALL_OFF: ManosPublicFlags = {
  master: false,
  directory: false,
  map: false,
  central: false,
  share: false,
  marketing: false,
};

/** Build the public CF URL from the project id (defaults to env), or null when absent. */
export function manosFlagsUrl(
  projectId: string | undefined = import.meta.env.VITE_FIREBASE_PROJECT_ID,
): string | null {
  if (!projectId) return null;
  return `https://us-central1-${projectId}.cloudfunctions.net/getManosPublicFlags`;
}

/** Coerce an arbitrary JSON payload into ManosPublicFlags (strict === true; default OFF). */
export function normalizeManosFlags(data: unknown): ManosPublicFlags {
  if (!data || typeof data !== 'object') return { ...MANOS_FLAGS_ALL_OFF };
  const d = data as Record<string, unknown>;
  const on = (k: keyof ManosPublicFlags) => d[k] === true;
  return {
    master: on('master'),
    directory: on('directory'),
    map: on('map'),
    central: on('central'),
    share: on('share'),
    marketing: on('marketing'),
  };
}

/** Fetch the resolved public flags from the CF. Fail-closed to all-OFF on any error. */
export async function fetchManosFlags(
  signal?: AbortSignal,
  url: string | null = manosFlagsUrl(),
): Promise<ManosPublicFlags> {
  if (!url) return { ...MANOS_FLAGS_ALL_OFF };
  let res: Response;
  try {
    res = await fetch(url, { signal });
  } catch {
    // Fail-closed (marketing hidden). A fetch-boundary failure — an abort (unmount) or a
    // benign network error (offline / CORS / CF cold-start → TypeError: Failed to fetch)
    // — is expected and non-actionable on a public landing page, so it is NOT captured
    // (QUUL-CLIENT-75, ~200 events, 0 users). The safe path (all-OFF) is already taken.
    return { ...MANOS_FLAGS_ALL_OFF };
  }
  if (!res.ok) return { ...MANOS_FLAGS_ALL_OFF };
  try {
    return normalizeManosFlags(await res.json());
  } catch (error) {
    // An error AFTER a 200 (malformed body / unexpected shape) is not a routine network
    // blip — keep capturing at low severity so a real CF regression still surfaces. An
    // abort during body read is still benign.
    if (!(error instanceof DOMException && error.name === 'AbortError')) {
      captureException(error, { lib: 'manosFlags', action: 'fetchManosFlags', severity: 'low' });
    }
    return { ...MANOS_FLAGS_ALL_OFF };
  }
}
